/**
 * The Ideas page's view and filters, kept in the URL so the List and Matrix
 * views share them, a filtered view can be linked, and other screens can link
 * in pre-filtered (Industries → `/business-opportunities?view=list&industry=<id>`, Dashboard
 * → `/business-opportunities?view=list&verdict=go`). The Matrix view is the default. Defaults
 * are left out of the URL.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Disposition, IdeaListParams, VerdictOutcome } from '@/api/types';

export type IdeaView = 'list' | 'matrix';
/** What a matrix dot's colour shows; its shape always shows the verdict. */
export type ColorBy = 'verdict' | 'industry';

const DISPOSITIONS: readonly Disposition[] = ['active', 'parked', 'archived'];
const OUTCOMES: readonly VerdictOutcome[] = ['go', 'conditional_go', 'no_go', 'researching', 'failed'];
const PAGE_SIZES = [25, 50, 100];
const DEFAULT_VIEW: IdeaView = 'matrix';
const DEFAULT_SORT = 'total';
const DEFAULT_PAGE_SIZE = 50;

export interface IdeaFilters {
  view: IdeaView;
  colorBy: ColorBy;
  /** The list query, search included. */
  params: IdeaListParams;
  search: string;
  /** Any filter or search narrowing the ideas (sorting and paging aside). */
  filtered: boolean;
  setView: (view: IdeaView) => void;
  setColorBy: (colorBy: ColorBy) => void;
  /** A change to what matches, or to its order, starts again at page 1. */
  refine: (patch: Partial<IdeaListParams>) => void;
  setPage: (page: number) => void;
}

function positive(value: string | null, fallback: number): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : fallback;
}

/** A link to the Ideas page with these filters. */
export function opportunitiesHref(filters: {
  view?: IdeaView;
  dispositions?: Disposition[];
  outcomes?: VerdictOutcome[];
  industries?: string[];
}): string {
  const query = new URLSearchParams();
  if (filters.view && filters.view !== DEFAULT_VIEW) query.set('view', filters.view);
  filters.dispositions?.forEach((d) => query.append('status', d));
  filters.outcomes?.forEach((o) => query.append('verdict', o));
  filters.industries?.forEach((i) => query.append('industry', i));
  const qs = query.toString();
  return qs ? `/business-opportunities?${qs}` : '/business-opportunities';
}

export interface IdeaQuery {
  view: IdeaView;
  colorBy: ColorBy;
  params: IdeaListParams;
}

/** Reads the view and filters from the URL; anything unknown falls back to its default. */
export function parseIdeaQuery(query: URLSearchParams): IdeaQuery & { search: string; filtered: boolean } {
  const view: IdeaView = query.get('view') === 'list' ? 'list' : DEFAULT_VIEW;
  const colorBy: ColorBy = query.get('color') === 'industry' ? 'industry' : 'verdict';
  const search = query.get('q') ?? '';
  const size = positive(query.get('size'), DEFAULT_PAGE_SIZE);
  const params: IdeaListParams = {
    dispositions: query.getAll('status').filter((d): d is Disposition => DISPOSITIONS.includes(d as Disposition)),
    outcomes: query.getAll('verdict').filter((o): o is VerdictOutcome => OUTCOMES.includes(o as VerdictOutcome)),
    industries: query.getAll('industry'),
    q: search,
    sort: query.get('sort') || DEFAULT_SORT,
    order: query.get('order') === 'asc' ? 'asc' : 'desc',
    page: positive(query.get('page'), 1),
    page_size: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE,
  };
  const filtered =
    Boolean(search) || params.industries.length > 0 || params.dispositions.length > 0 || params.outcomes.length > 0;
  return { view, colorBy, params, search, filtered };
}

/** The URL query for a view and its filters, defaults left out. */
export function toIdeaQuery({ view, colorBy, params }: IdeaQuery): URLSearchParams {
  const next = new URLSearchParams();
  if (view !== DEFAULT_VIEW) next.set('view', view);
  if (colorBy !== 'verdict') next.set('color', colorBy);
  if (params.q) next.set('q', params.q);
  params.dispositions.forEach((d) => next.append('status', d));
  params.outcomes.forEach((o) => next.append('verdict', o));
  params.industries.forEach((i) => next.append('industry', i));
  if (params.sort !== DEFAULT_SORT || params.order !== 'desc') {
    next.set('sort', params.sort);
    next.set('order', params.order);
  }
  if (params.page > 1) next.set('page', String(params.page));
  if (params.page_size !== DEFAULT_PAGE_SIZE) next.set('size', String(params.page_size));
  return next;
}

export function useIdeaFilters(): IdeaFilters {
  const [query, setQuery] = useSearchParams();
  const state = useMemo(() => parseIdeaQuery(query), [query]);

  // Filtering is not navigation: Back leaves the page rather than undoing keystrokes.
  const write = useCallback((next: IdeaQuery) => setQuery(toIdeaQuery(next), { replace: true }), [setQuery]);

  const current = { view: state.view, colorBy: state.colorBy, params: state.params };
  return {
    ...state,
    setView: (view) => write({ ...current, view }),
    setColorBy: (colorBy) => write({ ...current, colorBy }),
    refine: (patch) => write({ ...current, params: { ...state.params, ...patch, page: 1 } }),
    setPage: (page) => write({ ...current, params: { ...state.params, page } }),
  };
}
